import { apiClient } from '../utils/apiClient';

/**
 * Build an Error that keeps the Axios response attached.
 *
 * Callers need the raw payload for two reasons:
 *  - plan-limit 402s carry `data.upgrade_required`, which is what opens the
 *    upgrade modal (see useWorkspaces.createWorkspace). A bare `new Error()`
 *    drops `.response`, so that check silently reads `undefined` and the user
 *    gets a generic failure toast instead of the upgrade prompt.
 *  - the reason text lives in `data.error` on this API; `data.message` is only
 *    kept as a fallback for older/other endpoints.
 *
 * Same shape as apiRequestService.createRequest / collectionService.createCollection.
 */
function serverError(error, fallbackMessage) {
  const err = new Error(
    error.response?.data?.error || error.response?.data?.message || fallbackMessage
  );
  err.response = error.response;
  return err;
}

class WorkspaceService {
  // Get all workspaces for the current user
  async getWorkspaces() {
    try {
      const response = await apiClient.get('/workspaces');
      // Handle both old format (array) and new format (object with personal_workspaces/team_workspaces)
      if (Array.isArray(response.data)) {
        return response.data; // Backward compatibility
      } else {
        return response.data; // Return the full object with personal_workspaces and team_workspaces
      }
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch workspaces');
    }
  }

  // Get a specific workspace
  async getWorkspace(workspaceId) {
    try {
      const response = await apiClient.get(`/workspaces/${workspaceId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch workspace:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch workspace');
    }
  }

  // Create a new workspace
  async createWorkspace(workspaceData) {
    try {
      const response = await apiClient.post('/workspaces', workspaceData);
      return response.data;
    } catch (error) {
      console.error('Failed to create workspace:', error);
      throw serverError(error, 'Failed to create workspace');
    }
  }

  // Update a workspace
  async updateWorkspace(workspaceId, workspaceData) {
    try {
      console.log('🔍 WorkspaceService.updateWorkspace called with:');
      console.log('  - workspaceId:', workspaceId);
      console.log('  - workspaceData:', JSON.stringify(workspaceData, null, 2));
      
      const response = await apiClient.put(`/workspaces/${workspaceId}`, workspaceData);
      
      console.log('✅ WorkspaceService.updateWorkspace response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to update workspace:', error);
      console.error('  - Error response:', error.response?.data);
      throw serverError(error, 'Failed to update workspace');
    }
  }

  // Get workspace variable scopes
  async getVariableScopes(workspaceId) {
    try {
      const response = await apiClient.get(`/workspaces/${workspaceId}/variables`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch workspace variable scopes:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch workspace variable scopes');
    }
  }

  // Update workspace variable scopes
  async updateVariableScopes(workspaceId, payload) {
    try {
      const response = await apiClient.put(`/workspaces/${workspaceId}/variables`, payload);
      return response.data;
    } catch (error) {
      console.error('Failed to update workspace variable scopes:', error);
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Failed to update workspace variable scopes');
    }
  }

  // Batch-update the full sidebar tree (collection/folder/request nesting + order) for a
  // workspace in a single request. Payload comes from buildTreeLayoutPayload().
  async updateTreeLayout(workspaceId, layout) {
    try {
      const response = await apiClient.put(`/workspaces/${workspaceId}/sidebar-layout`, layout);
      return response.data;
    } catch (error) {
      console.error('Failed to update sidebar layout:', error);
      throw new Error(
        error.response?.data?.error || error.response?.data?.message || 'Failed to update sidebar layout'
      );
    }
  }

  // Back-compat alias — same endpoint, order-only payload from buildSidebarLayoutPayload().
  async updateSidebarLayout(workspaceId, layout) {
    return this.updateTreeLayout(workspaceId, layout);
  }

  async publishLocal(payload) {
    try {
      const response = await apiClient.post('/workspaces/publish-local', payload);
      return response.data;
    } catch (error) {
      console.error('Failed to publish local workspace:', error);
      // publish_local returns the same plan-limit 402 as create (workspace_controller).
      throw serverError(error, 'Failed to publish workspace');
    }
  }

  // Share a personal workspace with a team (creates a team library entry)
  async shareWithTeam(workspaceId, data) {
    try {
      const response = await apiClient.post(`/workspaces/${workspaceId}/share-with-team`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to share workspace with team:', error);
      throw serverError(error, 'Failed to share workspace with team');
    }
  }

  // Remove a workspace from a team's library
  async removeFromTeam(teamId, workspaceId) {
    try {
      const response = await apiClient.delete(`/teams/${teamId}/workspaces/${workspaceId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to remove workspace from team:', error);
      throw serverError(error, 'Failed to remove workspace from team');
    }
  }

  // Fork a workspace (deep copy collections and requests)
  async forkWorkspace(workspaceId) {
    try {
      const response = await apiClient.post(`/workspaces/${workspaceId}/fork`);
      return response.data;
    } catch (error) {
      console.error('Failed to fork workspace:', error);
      throw serverError(error, 'Failed to fork workspace');
    }
  }

  // Delete a workspace
  async deleteWorkspace(workspaceId) {
    try {
      const response = await apiClient.delete(`/workspaces/${workspaceId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to delete workspace:', error);
      throw serverError(error, 'Failed to delete workspace');
    }
  }

  // Add member to workspace
  async addMember(workspaceId, memberData) {
    try {
      const response = await apiClient.post(`/workspaces/${workspaceId}/members`, memberData);
      return response.data;
    } catch (error) {
      console.error('Failed to add member:', error);
      throw new Error(error.response?.data?.message || 'Failed to add member');
    }
  }

  // Remove member from workspace
  async removeMember(workspaceId, memberId) {
    try {
      const response = await apiClient.delete(`/workspaces/${workspaceId}/members/${memberId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to remove member:', error);
      throw new Error(error.response?.data?.message || 'Failed to remove member');
    }
  }

  // Update member role
  async updateMemberRole(workspaceId, memberId, role) {
    try {
      const response = await apiClient.put(`/workspaces/${workspaceId}/members/${memberId}/role`, { role });
      return response.data;
    } catch (error) {
      console.error('Failed to update member role:', error);
      throw new Error(error.response?.data?.message || 'Failed to update member role');
    }
  }

  // Clear any cached data (for logout cleanup)
  clearCache() {
    // Clear any cached workspace data if you implement caching in the future
    console.log('WorkspaceService cache cleared');
  }
}

export const workspaceService = new WorkspaceService();
